import { convertEmptyStringsToNull, convertIncomingPercentage, convertOutgoingPercentage } from "../../../pages/activityBuilder/modules/inputs/utils";
import { TransportEntryData, TransportEntryResponse } from "../../../pages/activityBuilder/modules/transport/transportTypes";
import { EntryCreatePayload, EntryDeletePayload, EntryUpdatePayload, ParentUpdatePayload, ParentResponse, ParentPayload } from "../../../types/modulesInterfaces";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createEntriesGetApi, createModuleGetApi } from "./factories/createModuleApi";

// Define constants for the module
const MODULE_TYPE = "Transport";
const TAG: TagTypes = "Transports";
const ENDPOINT = FEModulesObj[FEModules.Transport].path;

const ENTRY_MODULE_TYPE = "TransportEntries";
const TAG_ENTRY: TagTypes = "TransportEntries";
const TAG_ENTRY_DEFAULTS: TagTypes = "TransportEntriesDefaults";
export const ENTRY_ENDPOINT = "transport-entries";

const transportApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const transformEntriesResponse = (response: TransportEntryData[]) => {
	response.forEach(entry => convertIncomingPercentage(entry, ["transmission_loss_t2"]));
	return response.sort((a, b) => a.id - b.id);
};

const transportEntriesApiSlice = createEntriesGetApi<TransportEntryData[]>({
	apiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformEntriesResponse,
});

const publicTransportApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const publicTransportEntriesApiSlice = createEntriesGetApi<TransportEntryData[]>({
	apiSlice: publicApiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformEntriesResponse,
});

export const { useGetTransportQuery } = transportApiSlice;
export const { useGetTransportEntriesQuery, useGetTransportEntriesDefaultsQuery } = transportEntriesApiSlice;

export const { useGetTransportQuery: usePublicGetTransportQuery } = publicTransportApiSlice;
export const { 
	useGetTransportEntriesQuery: usePublicGetTransportEntriesQuery, 
	useGetTransportEntriesDefaultsQuery: usePublicGetTransportEntriesDefaultsQuery 
} = publicTransportEntriesApiSlice;

export const mutationApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		updateTransports: builder.mutation<ParentResponse, ParentUpdatePayload<ParentPayload>>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				convertEmptyStringsToNull(rest);
				return { url: `${ENDPOINT}/${id}/`, method: 'PUT', body: rest };
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: 'Transports', id: body?.id ?? 0 },
				{ type: "ModulesStatuses", id: activityId },
				{ type: 'ModuleResults', id: body?.id ? `transports-${body.id}` : undefined },
				{ type: 'ModuleHistory', id: body?.id ? `transports-${body.id}` : undefined },
			]
		}),
		updateTransportEntry: builder.mutation<TransportEntryResponse, EntryUpdatePayload<TransportEntryResponse>>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				convertEmptyStringsToNull(rest);
				convertOutgoingPercentage(rest, ["transmission_loss_t2"]);
				return {
					url: `${ENTRY_ENDPOINT}/${id}/`,
					method: 'PUT',
					body: rest
				};
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: 'TransportEntries', id: activityId },
				{ type: 'TransportEntriesDefaults', id: body?.id ? body?.id : undefined },
			]
		}),
		createTransportEntry: builder.mutation<TransportEntryResponse, EntryCreatePayload>({
			query: ({ activityId, parent }) => ({ 
				url: `${ENTRY_ENDPOINT}/`, 
				method: 'POST', 
				body: { activity: activityId, parent } 
			}),
			invalidatesTags: (_results, _error, { activityId, parent }) => [
				{ type: 'TransportEntries', id: activityId },
				{ type: 'ModuleResults', id: parent ? `transports-${parent}` : undefined },
				{ type: 'ModuleHistory', id: parent ? `transports-${parent}` : undefined },
				{ type: 'ModulesStatuses', id: activityId }
			]
		}),
		deleteTransportEntry: builder.mutation<void, EntryDeletePayload>({
			query: ({ id }) => ({ url: `${ENTRY_ENDPOINT}/${id}/`, method: 'DELETE' }),
			invalidatesTags: (_results, _error, { activityId, parent, id }) => [
				{ type: 'TransportEntries', id: activityId },
				{ type: 'ModuleResults', id: parent ? `transports-${parent}` : undefined },
				{ type: 'ModuleHistory', id: parent ? `transports-${parent}` : undefined },
				{ type: 'TransportEntriesDefaults', id },
				{ type: 'ModulesStatuses', id: activityId }
			]
		}),
	}),
});

export const {
	useUpdateTransportsMutation,
	useCreateTransportEntryMutation,
	useUpdateTransportEntryMutation,
	useDeleteTransportEntryMutation,
} = mutationApiSlice;